import { useCallback, useEffect, useState } from "react";
import { Loader2, CreditCard, Crown, CheckCircle2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { billingService, STATUS_LABELS } from "@/services/billingService";
import { publicService } from "@/services/publicService";
import { fmtDate } from "@/lib/datetime";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const badgeCls = (s) => ({
  active: "bg-emerald-100 text-emerald-700", trialing: "bg-blue-100 text-blue-700",
  complimentary: "bg-brand-gold/20 text-amber-700", cancel_at_period_end: "bg-amber-100 text-amber-700",
  grace_period: "bg-orange-100 text-orange-700", past_due: "bg-red-100 text-red-700",
  cancelled: "bg-secondary text-muted-foreground", expired: "bg-secondary text-muted-foreground",
  none: "bg-secondary text-muted-foreground",
}[s] || "bg-secondary text-muted-foreground");

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 border-b border-border py-2.5 last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="min-w-0 truncate text-right text-sm font-medium text-foreground">{value}</span>
  </div>
);

export const BillingSettings = () => {
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [liveBilling, setLiveBilling] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([billingService.subscription(), billingService.plans()]);
      setSub(s); setPlans(p.plans);
    } catch { toast.error("Could not load billing"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { publicService.siteConfig().then((c) => setLiveBilling(c.live_billing)).catch(() => {}); }, []);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("billing") === "success") toast.success("Checkout complete — your subscription will activate shortly.");
    if (q.get("billing") === "cancel") toast.info("Checkout cancelled.");
  }, []);

  const subscribe = async (plan_code) => {
    setBusy(true);
    try { const { checkout_url } = await billingService.checkout(plan_code); window.location.href = checkout_url; }
    catch (err) { toast.error(err?.message || "Could not start checkout"); setBusy(false); }
  };
  const manage = async () => { setBusy(true); try { const { portal_url } = await billingService.portal(); window.location.href = portal_url; } catch (err) { toast.error(err?.message || "Could not open portal"); setBusy(false); } };
  const cancel = async () => { setBusy(true); try { setSub(await billingService.cancel()); setConfirmOpen(false); toast.success("Cancellation scheduled for the end of your paid period"); } catch (err) { toast.error(err?.message || "Could not schedule cancellation"); } finally { setBusy(false); } };
  const reactivate = async () => { setBusy(true); try { setSub(await billingService.reactivate()); toast.success("Subscription resumed — normal renewal restored"); } catch (err) { toast.error(err?.message); } finally { setBusy(false); } };

  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>;

  const hasSub = sub && !["none", "incomplete"].includes(sub.status);
  const canManage = sub?.stripe_customer_id && !sub?.complimentary;

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="billing-settings">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CreditCard className="h-4 w-4 text-accent" /> Current plan</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge className={badgeCls(sub?.status)} data-testid="billing-status">{STATUS_LABELS[sub?.status] || sub?.status}</Badge>
            {sub?.founding_agent && <Badge className="bg-brand-navy text-white" data-testid="founding-badge"><Crown className="mr-1 h-3 w-3 text-brand-gold" /> Founding Agent #{String(sub.founding_number || 0).padStart(3, "0")}</Badge>}
          </div>
          {hasSub ? (
            <>
              <Row label="Plan" value={sub.plan_code || "—"} />
              {sub.billing_interval && <Row label="Billing" value={sub.billing_interval === "year" ? "Annual" : "Monthly"} />}
              {sub.status === "trialing" && sub.trial_end && <Row label="Trial ends" value={fmtDate(sub.trial_end)} />}
              {sub.current_period_end && <Row label="Current period ends" value={fmtDate(sub.current_period_end)} />}
              {sub.status === "cancel_at_period_end" && <Row label="Access until" value={fmtDate(sub.current_period_end)} />}
              {sub.status === "grace_period" && sub.grace_period_end && <Row label="Grace period ends" value={fmtDate(sub.grace_period_end)} />}
              {sub.complimentary && <Row label="Complimentary" value={sub.complimentary_reason || "granted"} />}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">You don't have an active subscription yet. Choose a plan to get started with a 14-day free trial.</p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {canManage && <Button variant="outline" size="sm" onClick={manage} disabled={busy} data-testid="billing-manage"><ExternalLink className="h-4 w-4" /> Manage billing</Button>}
            {canManage && sub.status !== "cancel_at_period_end" && <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(true)} disabled={busy} data-testid="billing-cancel" className="text-muted-foreground">Cancel subscription</Button>}
            {canManage && sub.status === "cancel_at_period_end" && <Button size="sm" onClick={reactivate} disabled={busy} data-testid="billing-reactivate" className="bg-brand-navy hover:bg-brand-deep-blue">Resume subscription</Button>}
          </div>
          {sub?.status === "cancel_at_period_end" && (
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="cancellation-scheduled-notice">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Cancellation scheduled. You keep full access until <strong>{fmtDate(sub.current_period_end)}</strong>, and your subscription will not renew after that date. You can resume anytime before then.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Cancellation takes effect at the <strong>end of your current paid period</strong>. You will keep full AgentHQ access until <strong data-testid="cancel-access-until">{fmtDate(sub?.current_period_end)}</strong>.</p>
                <p>Your payment is <strong>non-refundable</strong> — there are no prorated or partial-period refunds. Your subscription simply will not renew after that date, and your account and business data are not deleted.</p>
                {sub?.founding_agent && (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" data-testid="founding-cancel-warning">
                    Your $19/month Founding Agent rate remains protected through your current paid subscription period. If your subscription expires, your locked Founding Agent rate will be forfeited. Returning later will require subscribing at the then-current standard rate.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-keep">Keep my subscription</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); cancel(); }} disabled={busy} data-testid="cancel-confirm-proceed" className="bg-brand-navy hover:bg-brand-deep-blue">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Schedule cancellation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!hasSub && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="billing-plans">
          {plans.map((p) => (
            <Card key={p.plan_code} className={p.founding ? "border-brand-gold/50" : ""}>
              <CardContent className="space-y-3 p-5">
                {p.founding && <Badge className="bg-brand-navy text-white"><Crown className="mr-1 h-3 w-3 text-brand-gold" /> {p.remaining} of 100 left</Badge>}
                <div><p className="font-heading text-lg font-bold text-foreground">{p.name}</p><p className="text-2xl font-bold text-brand-navy">{p.price}</p></div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  <li className="flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> Full AgentHQ access</li>
                  {p.trial_days > 0 && <li className="flex gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> {p.trial_days}-day free trial</li>}
                </ul>
                <Button className="w-full bg-brand-navy hover:bg-brand-deep-blue" onClick={() => subscribe(p.plan_code)} disabled={busy} data-testid={`subscribe-${p.plan_code}`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Choose plan"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">Payments are processed securely by Stripe{liveBilling ? "" : " (test mode)"}. RESIDENNCE never stores your card details.</p>
    </div>
  );
};
